import * as React from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCheck, Plus, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type {
  Evaluation,
  EvaluationScore,
  EvaluationTemplate,
  TechStackSnapshot,
} from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { ScaleLegend, scaleLabelFor } from '@/components/scale-legend'
import { CategoryTrend, type TrendSeries } from '@/components/category-trend'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * Every run of one template, area by area, oldest first.
 *
 * Built per template rather than per page, because two templates on one set of
 * axes would be meaningless — SWEBOK's ten areas and the Process assessment's
 * seven are different questions that happen to share a 1–5 scale.
 */
interface TemplateTrend {
  template: EvaluationTemplate
  runs: number
  series: TrendSeries[]
}

/**
 * One line in the list, which now covers two different things: a scored
 * evaluation (SWEBOK, Process) and a Tech Stack review, which has no scores at
 * all and lives on its own tables (migrations/0021_tech_stack.sql). They share
 * this page because from the team's side they are the same act — "we sat down in
 * March and assessed ourselves" — but `kind` decides the route, the progress
 * wording, and whether there is an average to show at all.
 */
interface Row {
  kind: 'evaluation' | 'tech-stack'
  id: string
  name: string
  /** conducted_on for an evaluation, taken_on for a review. */
  on: string
  status: Evaluation['status']
  facilitator: string | null
  /** What to call the thing, under the name. */
  templateName: string
  template: EvaluationTemplate | null
  done: number
  total: number
  average: number | null
}

export function EvaluationsPage() {
  const { currentTeam, isLead } = useAuth()
  const teamId = currentTeam?.id
  const [creating, setCreating] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId)
      return {
        rows: [] as Row[],
        templates: [] as EvaluationTemplate[],
        questionCount: 0,
        trends: [] as TemplateTrend[],
      }

    const [evals, templates, snaps, questions] = await Promise.all([
      sb().from('evaluations').select('*').eq('team_id', teamId).order('conducted_on', { ascending: false }),
      // Global templates (team_id IS NULL) plus this team's own. RLS already
      // limits what comes back; the filter keeps other teams' names out of the
      // picker for DPL admins, who can legitimately see them all. Team
      // templates only: an individual one (the SWEBOK self-assessment, 0067) is
      // taken on its own page, and the database refuses it here anyway.
      sb().from('evaluation_templates').select('*').or(`team_id.is.null,team_id.eq.${teamId}`).eq('is_active', true).eq('audience', 'team').order('name'),
      sb().from('tech_stack_snapshots').select('*').eq('team_id', teamId).order('taken_on', { ascending: false }),
      // The catalog is global and small; a head count is all the list needs.
      sb().from('tech_stack_questions').select('id', { count: 'exact', head: true }),
    ])
    if (evals.error) throw evals.error
    if (templates.error) throw templates.error
    if (snaps.error) throw snaps.error
    if (questions.error) throw questions.error

    const evaluations = (evals.data ?? []) as Evaluation[]
    const tpls = (templates.data ?? []) as EvaluationTemplate[]

    // How many criteria each template has, so the list can show progress
    // without loading every criterion row per evaluation.
    const totals = new Map<string, number>()
    for (const t of tpls) {
      const { count, error: cerr } = await sb()
        .from('evaluation_criteria')
        .select('id, evaluation_categories!inner(template_id)', { count: 'exact', head: true })
        .eq('evaluation_categories.template_id', t.id)
      if (cerr) throw cerr
      totals.set(t.id, count ?? 0)
    }

    let scores: EvaluationScore[] = []
    if (evaluations.length > 0) {
      const { data: s, error: serr } = await sb()
        .from('evaluation_scores')
        .select('*')
        .in('evaluation_id', evaluations.map((e) => e.id))
      if (serr) throw serr
      scores = (s ?? []) as EvaluationScore[]
    }

    const snapshots = (snaps.data ?? []) as TechStackSnapshot[]
    const questionCount = questions.count ?? 0

    // How many questions each review has answered. One query for all of them
    // rather than one per row.
    const answered = new Map<string, number>()
    if (snapshots.length > 0) {
      const { data: a, error: aerr } = await sb()
        .from('tech_stack_answers')
        .select('snapshot_id, choices')
        .in('snapshot_id', snapshots.map((s) => s.id))
      if (aerr) throw aerr
      for (const row of (a ?? []) as { snapshot_id: string; choices: string[] }[]) {
        // A row with nothing ticked is a question opened and left alone.
        if (row.choices.length === 0) continue
        answered.set(row.snapshot_id, (answered.get(row.snapshot_id) ?? 0) + 1)
      }
    }

    const rows: Row[] = [
      ...evaluations.map((e): Row => {
        const mine = scores.filter((s) => s.evaluation_id === e.id)
        const avg = mine.length > 0 ? mine.reduce((a, b) => a + Number(b.score), 0) / mine.length : null
        const tpl = tpls.find((t) => t.id === e.template_id) ?? null
        return {
          kind: 'evaluation',
          id: e.id,
          name: e.name,
          on: e.conducted_on,
          status: e.status,
          facilitator: e.facilitator,
          templateName: tpl?.name ?? 'Evaluation',
          template: tpl,
          done: mine.length,
          total: totals.get(e.template_id) ?? 0,
          average: avg,
        }
      }),
      ...snapshots.map((s): Row => ({
        kind: 'tech-stack',
        id: s.id,
        name: s.name,
        on: s.taken_on,
        status: s.status,
        facilitator: s.facilitator,
        templateName: 'Tech Stack review',
        template: null,
        done: answered.get(s.id) ?? 0,
        total: questionCount,
        // No average, and none is coming: a stack is a list of things, not a
        // number. The card renders nothing here rather than a zero.
        average: null,
      })),
    ].sort((a, b) => b.on.localeCompare(a.on))

    // ---- Trend: how each area moved across every run of a template ----------
    //
    // Costs one query per template that has runs, and reuses `scores`, which is
    // already every score for every evaluation on this page. Skipped entirely
    // for a template with fewer than two runs: a single measurement is a
    // position, not a trend, and drawing one dot under the heading "Trend"
    // overstates what the team knows about itself.
    const trends: TemplateTrend[] = []
    for (const t of tpls) {
      const runs = evaluations
        .filter((e) => e.template_id === t.id)
        .slice()
        .sort((a, b) => a.conducted_on.localeCompare(b.conducted_on))
      if (runs.length < 2) continue

      const [cats, crits] = await Promise.all([
        sb()
          .from('evaluation_categories')
          .select('id, name, display_order')
          .eq('template_id', t.id)
          .order('display_order'),
        // Same !inner trick the criteria count above uses — criteria carry no
        // template_id of their own, only a category.
        sb()
          .from('evaluation_criteria')
          .select('id, category_id, evaluation_categories!inner(template_id)')
          .eq('evaluation_categories.template_id', t.id),
      ])
      if (cats.error) throw cats.error
      if (crits.error) throw crits.error

      const categories = (cats.data ?? []) as { id: string; name: string; display_order: number }[]
      const criteria = (crits.data ?? []) as { id: string; category_id: string }[]

      const series: TrendSeries[] = categories.map((cat) => {
        const own = new Set(criteria.filter((c) => c.category_id === cat.id).map((c) => c.id))
        return {
          label: cat.name,
          points: runs.map((run) => {
            const vals = scores
              .filter((s) => s.evaluation_id === run.id && own.has(s.criterion_id))
              .map((s) => Number(s.score))
            return {
              on: run.conducted_on,
              // Averaged over what this run actually scored, matching the
              // detail page — a half-finished run reads as the average of its
              // answered questions rather than being dragged down by blanks.
              value: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
              runName: run.name,
            }
          }),
        }
      })

      // An area nobody has ever scored would draw an empty facet in a grid of
      // real ones, which reads as a rendering bug rather than as missing data.
      const withData = series.filter((s) => s.points.some((p) => p.value != null))
      if (withData.length > 0) trends.push({ template: t, runs: runs.length, series: withData })
    }

    return { rows, templates: tpls, questionCount, trends }
  }, [teamId])

  async function remove(e: Row) {
    const what = e.kind === 'tech-stack' ? 'answers' : 'scores'
    if (!window.confirm(`Delete “${e.name}”? Its ${what} go with it.`)) return
    const table = e.kind === 'tech-stack' ? 'tech_stack_snapshots' : 'evaluations'
    const { error: err } = await sb().from(table).delete().eq('id', e.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  return (
    <Page
      title="Evaluations"
      description="Where the team stands, and when it was assessed. Run a new one any time."
      actions={
        <Button onClick={() => setCreating(true)}>
          <Plus /> New evaluation
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : (data?.rows.length ?? 0) === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="No evaluations yet"
          description="Start with the SWEBOK evaluation to set a baseline, or take a Tech Stack review to record what the team runs on. Either way, re-run it later to show movement."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus /> Start an evaluation
            </Button>
          }
        />
      ) : (
        <div className="space-y-5">
          {/* Above the list on purpose: the list answers "when did we assess
              ourselves", and this answers "did any of it move" — which is the
              question someone opening this page after a year actually has. */}
          {data?.trends.map((t) => (
            <Card key={t.template.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t.template.name} over time</CardTitle>
                <CardDescription>
                  Every area across {t.runs} runs, oldest to newest, on the{' '}
                  {t.template.scale_min}–{t.template.scale_max} scale. The latest value is
                  printed; hover a point for its run.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CategoryTrend series={t.series} template={t.template} />
              </CardContent>
            </Card>
          ))}

          <div className="space-y-2.5">
          {data?.rows.map((e) => {
            const pct = e.total === 0 ? 0 : Math.round((e.done / e.total) * 100)
            const href = e.kind === 'tech-stack' ? `/tech-stack/${e.id}` : `/evaluations/${e.id}`
            return (
              <Card key={e.id} className="transition-colors hover:border-primary/40">
                <CardContent className="flex flex-wrap items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={href} className="font-medium hover:underline">
                        {e.name}
                      </Link>
                      <Badge
                        variant={e.status === 'complete' ? 'success' : e.status === 'draft' ? 'muted' : 'warning'}
                        className="text-[10px]"
                      >
                        {e.status === 'in_progress' ? 'in progress' : e.status}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>Conducted {formatDate(e.on)}</span>
                      <span>{e.templateName}</span>
                      {e.facilitator && <span>Facilitated by {e.facilitator}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Progress value={pct} className="h-1.5 max-w-[220px]" />
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {e.done}/{e.total} {e.kind === 'tech-stack' ? 'answered' : 'scored'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {e.average != null && e.template && (
                      <div className="text-right">
                        <div className="text-2xl font-semibold tabular-nums leading-none">
                          {e.average.toFixed(1)}
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {scaleLabelFor(e.template, e.average) ?? `avg of ${e.template.scale_max}`}
                        </div>
                      </div>
                    )}
                    {isLead && (
                      <Button variant="ghost" size="icon-sm" onClick={() => void remove(e)} aria-label={`Delete ${e.name}`}>
                        <Trash2 />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
          </div>
        </div>
      )}

      {creating && teamId && (
        <NewEvaluationDialog
          teamId={teamId}
          templates={data?.templates ?? []}
          techStackQuestions={data?.questionCount ?? 0}
          existingCount={data?.rows.length ?? 0}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

/**
 * The Tech Stack review isn't an evaluation_template — it has no scale and its
 * own tables — but it IS one of the things you can start from this dialog, so it
 * appears in the same picker under a reserved value.
 */
const TECH_STACK = 'tech-stack'

function NewEvaluationDialog({
  teamId,
  templates,
  techStackQuestions,
  existingCount,
  onClose,
  onSaved,
}: {
  teamId: string
  templates: EvaluationTemplate[]
  techStackQuestions: number
  existingCount: number
  onClose: () => void
  onSaved: () => void
}) {
  // Default to SWEBOK — the one an accelerator opens with.
  const preferred = templates.find((t) => t.slug === 'swebok') ?? templates[0]
  const [templateId, setTemplateId] = React.useState(preferred?.id ?? '')
  const [name, setName] = React.useState('')
  const [conductedOn, setConductedOn] = React.useState(() => new Date().toISOString().slice(0, 10))
  const [facilitator, setFacilitator] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty({ templateId, name, conductedOn, facilitator }), onClose)

  const techStack = templateId === TECH_STACK
  const template = templates.find((t) => t.id === templateId)
  const label = techStack ? 'Tech Stack' : template?.name

  // Suggest a name that distinguishes runs of the same thing, since the whole
  // point is comparing this one against the last.
  const suggested = label
    ? existingCount === 0
      ? `${label} — baseline`
      : `${label} — ${new Date(conductedOn).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`
    : ''

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!templateId) return setError('Pick a template.')
    setBusy(true)
    setError(null)
    try {
      // Two different tables behind one dialog: a Tech Stack review has no
      // template and no scores, so it is not an evaluations row.
      const { error: err } = techStack
        ? await sb().from('tech_stack_snapshots').insert({
            team_id: teamId,
            name: name.trim() || suggested,
            taken_on: conductedOn,
            facilitator: facilitator.trim() || null,
            status: 'in_progress',
          })
        : await sb().from('evaluations').insert({
            team_id: teamId,
            template_id: templateId,
            name: name.trim() || suggested,
            conducted_on: conductedOn,
            facilitator: facilitator.trim() || null,
            status: 'in_progress',
          })
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this evaluation" />
        {/* min-w-0: the form is a grid item, so without it the scale legend's
            intrinsic width would stretch the dialog past its max width. */}
        <form onSubmit={save} className="min-w-0">
          <DialogHeader>
            <DialogTitle>New evaluation</DialogTitle>
            <DialogDescription>
              Creates a dated run of a template. Scores are entered on the next screen.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tpl">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger id="tpl">
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.version ? ` (${t.version})` : ''}
                    </SelectItem>
                  ))}
                  {techStackQuestions > 0 && (
                    <SelectItem value={TECH_STACK}>Tech Stack review</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {techStack ? (
                <p className="text-xs text-muted-foreground">
                  What the team actually runs on — languages, data, hosting, delivery tooling.
                  {' '}
                  {techStackQuestions} questions, answered with checkboxes. No scores: a stack is a
                  list, and the useful comparison is what joined it and what left.
                </p>
              ) : (
                <>
                  {template?.description && (
                    <p className="text-xs text-muted-foreground">{template.description}</p>
                  )}
                  {template && (
                    <div className="pt-1">
                      <p className="mb-1 text-xs font-medium text-muted-foreground">Scored on</p>
                      <ScaleLegend template={template} />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ename">Name</Label>
              <Input id="ename" value={name} onChange={(e) => setName(e.target.value)} placeholder={suggested} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="edate">Conducted on</Label>
                <Input id="edate" type="date" value={conductedOn} onChange={(e) => setConductedOn(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="efac">Facilitator</Label>
                <Input id="efac" value={facilitator} onChange={(e) => setFacilitator(e.target.value)} placeholder="Optional" />
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !templateId}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
